import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const LOCALES = { it: "it-IT", en: "en-GB" };

export function formatNewsDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(LOCALES[lang] || "it-IT", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

export function localizeNews(n, lang) {
  const t = n.translations?.[lang];
  return t?.title ? { ...n, ...t } : n;
}

export default function News() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/news`).then((res) => setItems(res.data.items || [])).catch(() => {});
  }, []);

  return (
    <section id="news" data-testid="news-section" className="py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary"
        >
          {t.news.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-5xl"
        >
          {t.news.title}
        </motion.h2>

        <div className="mt-16 border-t border-white/10">
          {items.map((raw, i) => {
            const n = localizeNews(raw, lang);
            return (
            <motion.article
              key={n.news_id}
              data-testid={`news-item-${i}`}
              onClick={() => navigate(`/news/${n.news_id}`)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              className="group cursor-pointer border-b border-white/10 py-7 transition-colors duration-300 hover:bg-white/[0.03]"
            >
              <div className="flex flex-col gap-4 px-2 md:flex-row md:items-center md:gap-10 md:px-6">
                <span className="w-32 shrink-0 text-[12px] font-semibold uppercase tracking-[0.2em] text-white/40">
                  {formatNewsDate(n.created_at, lang)}
                </span>
                {n.image_path && (
                  <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-white/10">
                    <img
                      src={`${API}/news-images/${n.image_path}`}
                      alt={n.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-serif text-2xl leading-snug tracking-tight text-white transition-colors duration-300 group-hover:text-primary md:text-3xl">
                    {n.title}
                  </h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/55 md:text-base">
                    {n.excerpt}
                  </p>
                </div>
                <ArrowUpRight
                  size={22}
                  className="shrink-0 self-start text-white/30 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-primary md:self-center"
                />
              </div>
            </motion.article>
            );
          })}
          {items.length === 0 && (
            <p className="py-10 text-sm text-white/40" data-testid="news-empty">
              {{ it: "Nessuna news pubblicata al momento.", en: "No news published yet." }[lang]}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
